<template lang="pug">
#person-tabs(v-if='availableTabs.length > 0')
  nav.person-tab-bar(role='tablist', :aria-label='t("person.sections")')
    button.person-tab-button(
      v-for='(tabItem, index) in availableTabs'
      :key='tabItem.value'
      :id='tabId(tabItem.value)'
      :class='{ "person-tab-button--active": tab === tabItem.value }'
      :aria-selected='tab === tabItem.value'
      :aria-controls='tabPanelId(tabItem.value)'
      :tabindex='tab === tabItem.value ? 0 : -1'
      role='tab'
      type='button'
      @click='tab = tabItem.value'
      @keydown='handleTabKeydown($event, index)'
    )
      span {{ tabItem.label }}
  .person-tab-content(
    :id='tabPanelId(tab)'
    role='tabpanel'
    :aria-labelledby='tabId(tab)'
    tabindex='0'
  )
    person-about(v-if='tab === "about"', :about='person.en.about')
    person-projects(v-else-if='tab === "projects"', :projects='projects')
    person-activities(v-else-if='tab === "activities"', :activities='person.en.activities')
    person-awards(v-else-if='tab === "awards"', :awardsInfo='person.en.awards')
    person-thesis-tab(
      v-else-if='tab === "theses"'
      :theses='theses',
      :showSupervisor='false',
      :showTutor='false',
      :descriptionOpenByDefault='false',
      :personContext='person'
    )
</template>

<script setup lang="ts">
import type { PersonalProjects } from '~/interfaces/project'
import type { PersonInterface } from '~/interfaces/person'
import type { Thesis } from '~/models/thesis'

defineOptions({
  name: 'PersonTabs',
})

const { t } = useI18n()

const props = defineProps({
  person: {
    type: Object as () => PersonInterface,
    required: true,
  },
  projects: {
    type: Object as () => PersonalProjects,
    required: true,
  },
  bibtexString: {
    type: String,
    required: true,
  },
  theses: {
    type: Array as () => Array<Thesis>,
    required: true,
  },
})

const tab = ref('')

const availableTabs = computed(() => {
  const tabs: Array<{ value: string; label: string }> = []

  if (aboutSectionPopulated()) {
    tabs.push({ value: 'about', label: t('person.about') })
  }
  if (projectsPopulated()) {
    tabs.push({ value: 'projects', label: t('person.projects') })
  }
  if (activitiesPopulated()) {
    tabs.push({ value: 'activities', label: t('person.activities') })
  }
  if (awardsPopulated()) {
    tabs.push({ value: 'awards', label: t('person.awards') })
  }
  if (thesesPopulated()) {
    tabs.push({ value: 'theses', label: t('person.theses') })
  }

  return tabs
})

watchEffect(() => {
  if (!availableTabs.value.some((item) => item.value === tab.value)) {
    tab.value = availableTabs.value[0]?.value ?? ''
  }
})

function tabId(value: string) {
  return `person-tab-${value}`
}

function tabPanelId(value: string) {
  return `person-tabpanel-${value}`
}

function focusTab(index: number) {
  const nextTab = availableTabs.value[index]

  if (!nextTab) {
    return
  }

  tab.value = nextTab.value
  nextTick(() => {
    document.getElementById(tabId(nextTab.value))?.focus()
  })
}

function handleTabKeydown(event: KeyboardEvent, index: number) {
  if (event.key === 'ArrowRight') {
    event.preventDefault()
    focusTab((index + 1) % availableTabs.value.length)
  }

  if (event.key === 'ArrowLeft') {
    event.preventDefault()
    focusTab(
      (index - 1 + availableTabs.value.length) % availableTabs.value.length,
    )
  }

  if (event.key === 'Home') {
    event.preventDefault()
    focusTab(0)
  }

  if (event.key === 'End') {
    event.preventDefault()
    focusTab(availableTabs.value.length - 1)
  }
}

function awardsPopulated() {
  if (typeof props.person.en.awards === 'undefined') {
    return false
  }
  return !!props.person.en.awards.length
}

function projectsPopulated() {
  if (typeof props.projects === 'undefined') {
    return false
  }
  return (
    !!props.projects.inProgress.length ||
    !!props.projects.definitelyFinished.length
  )
}

function aboutSectionPopulated() {
  if (typeof props.person.en.about === 'undefined') {
    return false
  }
  const { about } = props.person.en
  return (
    (about.researchFoci && !!about.researchFoci.length) ||
    (about.education && !!about.education.length) ||
    (about.positions && !!about.positions.length) ||
    (about.appointments && !!about.appointments.length) ||
    false
  )
}

function thesesPopulated() {
  return props.theses != null && props.theses.length > 0
}

function activitiesPopulated() {
  if (typeof props.person.en.activities === 'undefined') {
    return false
  }

  const { activities } = props.person.en
  return !!activities.reviewer.length || !!activities.programCommittee.length
}
</script>

<style scoped>
#person-tabs {
  display: grid;
  gap: 1rem;
}

.person-tab-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  border-bottom: 1px solid var(--border-default);
  padding-bottom: 0.35rem;
}

.person-tab-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 2.75rem;
  padding: 0 0.9rem;
  border: 0;
  background: transparent;
  color: var(--color-text-strong);
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.76rem;
  font-weight: 700;
}

.person-tab-button::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -0.35rem;
  width: 24px;
  height: 2px;
  background: var(--brand-primary);
  transform: translateX(-50%) scaleX(0);
  transform-origin: center;
  transition: transform 0.2s ease;
}

.person-tab-button:hover {
  color: var(--brand-primary);
}

.person-tab-button--active {
  color: var(--brand-primary);
}

.person-tab-button--active::after {
  transform: translateX(-50%) scaleX(1);
}

.person-tab-content {
  min-width: 0;
  overflow: visible;
}
</style>
